import { TEST_CONFIG } from 'js/ab_tests/tests/autotest_login_modal_2026_q3_v1';

import type { LoginModalEventDetail } from '../LoginModalManager';
import { LoginModalAutotestHooks, PartialConfigurableText } from '../types';
import { afterClickMapSpec } from './arms/after_click_map';
import { blurredTakeawaysSpec } from './arms/blurred_takeaways';
import { claimTicketSpec } from './arms/claim_ticket';
import { dataScopeSpec } from './arms/data_scope';
import { dismissalDockSpec } from './arms/dismissal_dock';
import { dossierSpec } from './arms/dossier';
import { downloadPreflightSpec } from './arms/download_preflight';
import { liveStatsSpec } from './arms/live_stats';
import { memoryLadderSpec } from './arms/memory_ladder';
import type { MemoryRung } from './memory';
import { quietModeSpec } from './arms/quiet_mode';
import { reassurancePillSpec } from './arms/reassurance_pill';
import { sendToDeskSpec } from './arms/send_to_desk';
import { sessionTrailSpec } from './arms/session_trail';
import { stepTwoSpec } from './arms/step_two';
import { whyFreeSpec } from './arms/why_free';

// Buckets derive from the generated test module so the registry can never
// drift from the yml: an arm name that isn't in the yml fails to typecheck.
export type Bucket = (typeof TEST_CONFIG.alternatives)[number];
export type Arm = Exclude<Bucket, 'control'>;

export type OpenContext = {
  detail: LoginModalEventDetail;
};

// The aux stamp keys an arm may contribute; autotest/autotest_bucket/
// modal_version stay harness-owned. Values are pinned unions — analytics
// GROUP BYs on these keys must never meet an ad-hoc dialect or a typo.
export type ArmRenderState = 'rendered' | 'fallback' | 'intent' | 'generic';

export type ArmStamp = {
  render_state?: ArmRenderState;
  memory_state?: MemoryRung;
};

export type ArmSpec = {
  slots?: (ctx: OpenContext) => LoginModalAutotestHooks['slots'];
  configuredTextOverride?: (
    ctx: OpenContext,
  ) => PartialConfigurableText | undefined;
  wrapOnSuccess?: LoginModalAutotestHooks['wrapOnSuccess'];
  onClose?: (ctx: OpenContext) => void;
  ctaDefaultExpanded?: (ctx: OpenContext) => boolean;
  // Resolved before the impression fires so render_state/memory_state ride
  // every stamped click; must be side-effect free (reads only).
  stamp?: (ctx: OpenContext) => ArmStamp | undefined;
  // The one sanctioned write-at-open seam (e.g. memory recordOpen). Runs
  // AFTER every read path (stamp, slots, configuredTextOverride,
  // ctaDefaultExpanded), so those always observe pre-open state.
  onOpen?: (ctx: OpenContext) => void;
};

// Wave-key buckets dispatch through these specs; smoke-key buckets never
// reach this table (harness gate) and keep rendering control. An arm whose
// predicates fail yields no hooks and renders control pixel-for-pixel.
export const ARMS: Partial<Record<Arm, ArmSpec>> = {
  reassurance_pill: reassurancePillSpec,
  data_scope: dataScopeSpec,
  after_click_map: afterClickMapSpec,
  why_free: whyFreeSpec,
  download_preflight: downloadPreflightSpec,
  step_two: stepTwoSpec,
  claim_ticket: claimTicketSpec,
  live_stats: liveStatsSpec,
  blurred_takeaways: blurredTakeawaysSpec,
  dossier: dossierSpec,
  memory_ladder: memoryLadderSpec,
  session_trail: sessionTrailSpec,
  dismissal_dock: dismissalDockSpec,
  send_to_desk: sendToDeskSpec,
  quiet_mode: quietModeSpec,
};
